import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  setDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';

class FirestoreService {
  // Users Collection
  async createUser(userData) {
    try {
      const userRef = doc(db, 'users', userData.uid);
      await setDoc(userRef, {
        ...userData,
        createdAt: serverTimestamp(),
        lastLoginAt: serverTimestamp()
      });
      return userRef.id;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  async getUser(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      return userSnap.exists() ? { id: userSnap.id, ...userSnap.data() } : null;
    } catch (error) {
      console.error('Error getting user:', error);
      throw error;
    }
  }

  async updateUser(userId, updateData) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  // PassOns Collection
  async createPassOn(passOnData) {
    try {
      const passOnRef = await addDoc(collection(db, 'passOns'), {
        ...passOnData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: true
      });
      return passOnRef.id;
    } catch (error) {
      console.error('Error creating pass-on:', error);
      throw error;
    }
  }

  async getPassOns(filters = {}) {
    try {
      let q = collection(db, 'passOns');
      
      // Apply filters
      if (filters.category) {
        q = query(q, where('category', '==', filters.category));
      }
      if (filters.userId) {
        q = query(q, where('userId', '==', filters.userId));
      }
      if (filters.isActive !== undefined) {
        q = query(q, where('isActive', '==', filters.isActive));
      }
      
      // Apply ordering and limit
      q = query(q, orderBy('createdAt', 'desc'));
      if (filters.limit) {
        q = query(q, limit(filters.limit));
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting pass-ons:', error);
      throw error;
    }
  }

  async getPassOn(passOnId) {
    try {
      const passOnRef = doc(db, 'passOns', passOnId);
      const passOnSnap = await getDoc(passOnRef);
      return passOnSnap.exists() ? { id: passOnSnap.id, ...passOnSnap.data() } : null;
    } catch (error) {
      console.error('Error getting pass-on:', error);
      throw error;
    }
  }

  async updatePassOn(passOnId, updateData) {
    try {
      const passOnRef = doc(db, 'passOns', passOnId);
      await updateDoc(passOnRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating pass-on:', error);
      throw error;
    }
  }

  async deletePassOn(passOnId) {
    try {
      const passOnRef = doc(db, 'passOns', passOnId);
      await deleteDoc(passOnRef);
    } catch (error) {
      console.error('Error deleting pass-on:', error);
      throw error;
    }
  }

  // PassOn History Collection
  async createPassOnHistory(historyData) {
    try {
      const historyRef = await addDoc(collection(db, 'passOnHistory'), {
        ...historyData,
        passedAt: serverTimestamp()
      });
      return historyRef.id;
    } catch (error) {
      console.error('Error creating pass-on history:', error);
      throw error;
    }
  }

  async getPassOnHistory(userId) {
    try {
      const q = query(
        collection(db, 'passOnHistory'),
        where('fromUserId', '==', userId),
        orderBy('passedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting pass-on history:', error);
      throw error;
    }
  }

  // Real-time listeners
  subscribeToPassOns(callback, filters = {}) {
    let q = collection(db, 'passOns');
    
    if (filters.category) {
      q = query(q, where('category', '==', filters.category));
    }
    if (filters.isActive !== undefined) {
      q = query(q, where('isActive', '==', filters.isActive));
    }
    
    q = query(q, orderBy('createdAt', 'desc'));
    
    return onSnapshot(q, (snapshot) => {
      const passOns = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      callback(passOns);
    });
  }

  subscribeToUser(userId, callback) {
    const userRef = doc(db, 'users', userId);
    return onSnapshot(userRef, (doc) => {
      if (doc.exists()) {
        callback({ id: doc.id, ...doc.data() });
      } else {
        callback(null);
      }
    });
  }
}

const firestoreService = new FirestoreService();
export default firestoreService;
